import { useState, useEffect } from "react"
import styled from "styled-components/macro"
import gsap from "gsap"
import { useLocation, useNavigate } from "react-router-dom"
import Logo from "@common/ui/Logo"
import { useProjectDataStore } from "@state/projectDataStore"
import { getColor } from "@utils/getColor"
import TextButton from "@common/ui/TextButton"
import { useStore } from "@state/store"
import ContentIndexControls from "../ContentIndexControls"

function Sidebar() {
  const {
    brandingData,
    navigationData,
    galleryData,
    mapData,
    residencesData,
    info1Data,
  } = useProjectDataStore((s) => ({
    brandingData: s.brandingData,
    navigationData: s.navigationData,
    galleryData: s.galleryData,
    mapData: s.mapData,
    residencesData: s.residencesData,
    info1Data: s.info1Data,
  }))
  const { categoriesVisible, residencePopup } = useStore((s) => ({
    categoriesVisible: s.categoriesVisible,
    residencePopup: s.residencePopup,
  }))
  const syncState = useStore((s) => s.syncState)
  const [categories, setCategories] = useState([])
  const [currentPage, setCurrentPage] = useState("")
  const logoWidth = navigationData?.logoWidth || 8
  const navigate = useNavigate()
  const { search, pathname } = useLocation()

  const menuItems = navigationData?.menuItems
    ? [
        ...navigationData.menuItems,
        {
          id: "6718c699b5c2c70026898852",
          include: "true",
          navLabel: "Story",
          pageType: "info2",
        },
      ]
    : []
  const routes = menuItems.filter((item) => item.include === "true") || []

  const gallery = [
    ...Array.from(
      new Set(galleryData?.images?.map((image) => image?.category)),
    ),
  ]

  const map = [
    ...Array.from(new Set(mapData?.categories?.map((category) => category))),
  ]

  const residences = [
    ...Array.from(
      new Set(residencesData?.categories?.map((category) => category)),
    ),
  ]

  const info1 = [
    ...Array.from(
      new Set(info1Data?.sections?.map((category) => category?.title)),
    ),
  ]

  const info2 = ["Community", "The Team"]

  const categoriesIndex = {
    gallery: gallery,
    // residences: residences,
    map: map,
    // info1: info1,
    info2: info2,
  }

  useEffect(() => {
    const currentPageSplit = pathname.substring(1)
    setCategories(categoriesIndex[currentPageSplit])
  }, [categoriesVisible])

  useEffect(() => {
    if (residencePopup && categories?.[0] !== residences[0]) {
      setCategories(residences)
    }
  }, [residencePopup])

  useEffect(() => {
    gsap.to(".links", {
      autoAlpha: !categoriesVisible ? 1 : 0,
      x: !categoriesVisible ? 0 : -10,
      duration: 0.6,
      ease: "power3.inOut",
      delay: 0.1,
    })
    gsap.to(".subLinks", {
      autoAlpha: !categoriesVisible ? 0 : 1,
      x: !categoriesVisible ? 20 : 0,
      duration: 0.6,
      ease: "power3.inOut",
      delay: 0.1,
    })
  }, [categoriesVisible])

  function animateSplash() {
    const t = gsap.timeline()

    t.to(".splashAnim", {
      autoAlpha: 1,
      ease: "power3.inOut",
      duration: 0.6,
    })
      .call(() => {
        navigate("/")
        setTimeout(() => {
          window.location.reload()
        }, 200)
      })
      .to(".splashAnim", {
        autoAlpha: 0,
        ease: "power3.inOut",
        duration: 0.6,
        delay: 0.6,
      })
  }

  return (
    <SidebarWrapper $bgColor={getColor(navigationData?.bgColor)}>
      <Logo
        width={logoWidth}
        onPointerUp={() => {
          animateSplash()
        }}
        style={{
          margin: "0 auto",
          padding: "4rem 0",
          pointerEvents: "auto",
        }}
      />
      <LinksWrapper className="links">
        {routes
          ?.filter(
            (route) => route?.include === "true" && route?.path !== "splash",
          )
          ?.map(({ navLabel, pageType }, index) => {
            return (
              <div
                key={index}
                className="navItem"
                onClick={() => {
                  const path = pathname.substring(1)
                  if (path === pageType) {
                    if (categoriesIndex[path]) {
                      syncState({
                        categoriesVisible: true,
                      })
                    }
                  }
                  navigate(`${pageType}${search}`)
                  setCurrentPage(`${pageType}${search}`)
                }}
              >
                <div
                  className={
                    pathname.substring(1) === pageType ? "lineActive" : "line"
                  }
                ></div>
                <TextButton
                  variant="text"
                  color={"#e1e1e1"}
                  style={{ letterSpacing: "5px" }}
                >
                  {navLabel}
                </TextButton>
              </div>
            )
          })}
      </LinksWrapper>
      <SubLinksWrapper className="subLinks">
        <div
          className="back"
          onClick={() => {
            syncState({ categoriesVisible: false })
            if (residencePopup) {
              syncState({ residencePopup: false })
            }
          }}
          style={{
            opacity: categoriesVisible ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          <img src="/assets/icons/back.svg" alt="" />
          Back
        </div>
        <ContentIndexControls
          categories={categories}
          className="sidebarCategories"
        />
        {/* <div
          className="backButton"
          onClick={() => syncState({ categoriesVisible: false })}
        >
          <img src="/assets/icons/back.svg" alt="" />
          <img src="/assets/icons/back.svg" alt="" />
        </div> */}
      </SubLinksWrapper>
    </SidebarWrapper>
  )
}

export default Sidebar

type TNavStyledProps = {
  $bgColor?: string
}

const SidebarWrapper = styled.nav<TNavStyledProps>`
  background-color: ${(p) => p.$bgColor || "var(--primary)"};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10rem;

  .backButton {
    width: 15%;
    position: absolute;
    bottom: 0;
    left: 0;
    opacity: 1;
    padding: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ffffff1b;
    transition: 0.3s;
    cursor: pointer;

    &:active {
      opacity: 0.5;
      transform: scale(0.98);
      transition: 0.3s;
    }

    img {
      object-fit: contain;
    }
  }

  .sidebarCategories {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: baseline;
    cursor: pointer;
    pointer-events: all;
    gap: 1rem;

    .buttons {
      font-family: PrimaryFont;
      color: white;
      opacity: 0.5;
      font-size: 1.2rem;
    }

    .active {
      color: white;
      opacity: 1;
    }
  }
`

const LinksWrapper = styled.div`
  position: absolute;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 0.75rem;

  padding: 2rem 4rem;

  .navItem {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: TertiaryFont;
    font-size: 1rem;
    text-transform: uppercase;
    font-weight: 100;

    .line {
      width: 0px;
      height: 2px;
      border-radius: 1rem;
      background-color: #ffffff;
      transition: 0.3s;
    }
    .lineActive {
      width: 40px;
      height: 2px;
      border-radius: 1rem;
      background-color: #c3c3c3;
      transition: 0.3s;
    }
  }

  > * {
    pointer-events: all;
  }
`
const SubLinksWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  opacity: 0;
  padding: 1rem 5rem;
  font-family: TertiaryFont;
  font-size: 100%;

  .back {
    margin-right: auto;
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: center;
    cursor: pointer;
    transition: 0.3s;
    color: #ffffff;
    font-size: 1.2rem;

    &:active {
      transform: scale(0.95);
      opacity: 0.5;
      transition: 0.3s;
    }
  }

  .navItem {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .line {
      width: 0px;
      height: 1px;
      border-radius: 1rem;
      background-color: #000000;
    }
  }

  > * {
    pointer-events: all;
  }
`
